import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  resolveEntitlements,
  type Entitlements,
} from '../core/billing/entitlements';
import {
  entitlementSourceOf,
  isSubscriptionAssigned,
  normalizeBillingConfig,
  normalizeBillingCustomer,
  normalizeSubscription,
} from '../core/billing/subscriptionDoc';
import { fetchBillingOverview } from '../services/billing/BillingService';
import type { BillingOverview } from '../core/billing/billingViews';
import { useAuth } from './AuthContext';

interface SubscriptionContextValue {
  overview: BillingOverview | null;
  entitlements: Entitlements;
  /** True when a real subscription row exists for this banker. */
  assigned: boolean;
  loading: boolean;
  /** True while billing is unknown; callers should not paywall yet. */
  unresolved: boolean;
  refresh: () => Promise<void>;
}

/** God and unauthenticated sessions are never gated by a subscription. */
const UNGATED: Entitlements = resolveEntitlements({ planId: 'platinum', status: 'active' });

const SubscriptionCtx = createContext<SubscriptionContextValue | null>(null);

function emptyOverview(uid: string): BillingOverview {
  return {
    assigned: false,
    subscription: normalizeSubscription(uid, null),
    customer: normalizeBillingCustomer(uid, null),
    config: normalizeBillingConfig(null),
    houseIds: [],
    seatCount: 0,
    invoices: [],
    payments: [],
    renewalAmountMinor: 0,
  };
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [loading, setLoading] = useState(false);

  const uid = user?.id ?? '';
  const role = user?.role ?? '';
  const gated = role === 'banker' || role === 'lister' || role === 'runner';
  // God reads its own billing overview for the sheet, but is never gated by it.
  const fetches = gated || role === 'god';

  const load = useCallback(async () => {
    if (!uid || !fetches) {
      setOverview(null);
      return;
    }
    setLoading(true);
    try {
      setOverview(await fetchBillingOverview(uid));
    } catch {
      // Billing must never block the app: an unreachable backend leaves the
      // subscription unresolved and the UI keeps working ungated.
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [uid, fetches]);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo<SubscriptionContextValue>(() => {
    const assigned = Boolean(
      overview?.assigned
      ?? (overview ? isSubscriptionAssigned(overview.subscription) : false),
    );
    // Unassigned bankers are not billed — full access, no paywall.
    const resolved = overview && gated && assigned
      ? resolveEntitlements(entitlementSourceOf(overview.subscription))
      : UNGATED;
    return {
      overview,
      entitlements: resolved,
      assigned,
      loading,
      unresolved: gated && overview === null,
      refresh: load,
    };
  }, [overview, loading, gated, load]);

  return (
    <SubscriptionCtx.Provider value={value}>
      {children}
    </SubscriptionCtx.Provider>
  );
}

export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionCtx);
  if (!ctx) {
    // Screens rendered outside the provider (tests, storybook) stay ungated.
    return {
      overview: null,
      entitlements: UNGATED,
      assigned: false,
      loading: false,
      unresolved: false,
      refresh: async () => {},
    };
  }
  return ctx;
}

export { emptyOverview };
